<template>
    <div class="main-div">
        <div class="header-div">
            <div class="title-div">
                <div class="icon-text-header">
                    <div class="icon-div">
                        <PhKey :size="20" class="text-zinc-700" />
                    </div>
                    <div>
                        <div class="title-section">
                            <p>Create passkey</p>
                        </div>
                        <p class="sub-title">Give your passkey a name so you can identify it later</p>
                    </div>
                </div>
                <div class="input-wrapper">
                    <div class="divider"></div>
                    <prime-input-text
                        v-model="passkeyName"
                        autofocus
                        class="w-full"
                        placeholder="e.g., MacBook Pro, Work Laptop"
                        aria-label="Passkey name"
                        maxlength="255"
                        @keyup.enter="handleCreate"
                    />
                    <!-- Creating state. Mounted throughout and sr-only while idle: a polite region
                         that appears already holding its text is not reliably announced. -->
                    <div :class="creating ? 'creating-content' : 'sr-only'" role="status">
                        <template v-if="creating">
                            <i class="pi pi-spin pi-spinner creating-spinner" aria-hidden="true"></i>
                            <span>Follow the prompts on your device to create a passkey...</span>
                        </template>
                    </div>
                    <!-- Error message -->
                    <div v-if="registerError" class="error-message" role="alert">
                        <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                        {{ registerError }}
                    </div>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="footer-actions">
                <PrimeButton
                    @click="closeDialog"
                    variant="secondary"
                    label="Cancel"
                    :disabled="creating"
                />
                <PrimeButton
                    label="Create passkey"
                    icon="pi pi-plus"
                    @click="handleCreate"
                    :loading="creating"
                    :disabled="!passkeyName.trim() || !canCreatePasskey || creating"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { PhKey } from '@phosphor-icons/vue';
import { startRegistration as startWebAuthnRegistration } from '@simplewebauthn/browser';
import { ModalNames } from '~/types/modals';
import { passkeyService } from '~/services/passkey-service';
import { useAuthStore } from '~/stores/auth';

const { showSuccess } = useGlobalToast();
const authStore = useAuthStore();

const dialogRef = getDialogRef(ModalNames.CREATE_PASSKEY);

const passkeyName = ref('');
const creating = ref(false);
const registerError = ref<string | null>(null);

const canCreatePasskey = computed(() => {
    return typeof window !== 'undefined' && 'PublicKeyCredential' in window;
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleCreate = async () => {
    if (!passkeyName.value.trim() || !canCreatePasskey.value) return;

    creating.value = true;
    registerError.value = null;

    try {
        const email = authStore.user?.identity?.email;
        if (!email) {
            throw new Error('User email not found');
        }

        // Step 1: Get registration options
        const options = await passkeyService.getRegistrationOptions(email);

        if (!options || !options.challenge || !options._stateToken) {
            registerError.value = 'Failed to get registration options from server.';
            creating.value = false;
            return;
        }

        const stateToken = options._stateToken;

        // Step 2: Create passkey using browser API
        let attestationResponse;
        try {
            attestationResponse = await startWebAuthnRegistration({ optionsJSON: options as any });
        } catch (err: unknown) {
            const webAuthnError = err as { name?: string; message?: string };

            if (webAuthnError.name === 'NotAllowedError') {
                registerError.value = 'Registration was cancelled or timed out.';
            } else if (webAuthnError.name === 'InvalidStateError') {
                registerError.value = 'This passkey already exists on your device.';
            } else if (webAuthnError.name === 'SecurityError') {
                registerError.value = 'Security error: The RP ID is not valid for this domain.';
            } else {
                registerError.value = 'Failed to create passkey: ' + (webAuthnError.message || webAuthnError.name || 'Unknown error');
            }
            creating.value = false;
            return;
        }

        if (!attestationResponse) {
            registerError.value = 'Failed to create passkey: No response from browser.';
            creating.value = false;
            return;
        }

        // Step 3: Send attestation to server
        await passkeyService.registerPasskey(attestationResponse, stateToken, passkeyName.value.trim());

        showSuccess('Success', 'Passkey created successfully');
        dialogRef.value.close({ created: true });
    } catch (err: unknown) {
        const errorMessage = (err instanceof Error && err.message) || 'Failed to create passkey. Please try again.';
        registerError.value = errorMessage;
        console.error('[Passkey] Registration error:', err);
    } finally {
        creating.value = false;
    }
};
</script>

<style lang="scss" scoped>
.main-div {
    @apply sm:max-w-xl sm:w-full w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto;
}
.header-div {
    @apply flex flex-col;
}
.title-div {
    @apply flex flex-col p-6 pb-0 items-start gap-1 self-stretch w-full;
}
.icon-text-header {
    @apply flex flex-row items-start gap-4 w-full;
}
.icon-div {
    @apply relative flex w-12 h-12 p-3 justify-center items-center rounded-full bg-gray-100;
}
.title-section {
    @apply self-stretch flex flex-row justify-between relative;

    p {
        @apply text-zinc-950 text-lg font-semibold leading-7;
    }
}
.sub-title {
    @apply text-zinc-600 text-sm font-normal leading-5;
}
.input-wrapper {
    @apply flex flex-col items-start gap-1 w-full sm:pl-16;
}
.divider {
    @apply h-px bg-zinc-300 mt-4 mb-3.5 w-full;
}
.creating-content {
    @apply flex items-center gap-3 text-gray-600;
}
.creating-spinner {
    @apply text-xl;
}
.error-message {
    @apply w-full mt-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2 text-sm;
}
.modal-footer {
    @apply flex flex-col justify-center items-end self-stretch pt-8;

    .footer-actions {
        @apply flex justify-end items-center gap-3 flex-1 px-6 pb-6;
    }
}
</style>
